import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { CityId } from "@/data/cities";

interface CityCtx {
  city: CityId;
  setCity: (c: CityId | string) => void;
}

const Ctx = createContext<CityCtx | null>(null);
const KEY = "what.city";

// Lightweight slug validation — any DB-backed slug is allowed at runtime.
// Keep CityId typing for the static-cities use cases that still need it.
const isValidSlug = (s: string) => /^[a-z0-9-]{2,40}$/.test(s);

export function CityProvider({ children }: { children: ReactNode }) {
  const [city, setCityState] = useState<CityId>("helsinki" as CityId);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("city");
      if (fromUrl && isValidSlug(fromUrl)) {
        setCityState(fromUrl as CityId);
        localStorage.setItem(KEY, fromUrl);
        return;
      }

      const saved = localStorage.getItem(KEY);
      if (saved && isValidSlug(saved)) setCityState(saved as CityId);
    } catch {}
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== KEY) return;
      const next = event.newValue;
      if (next && isValidSlug(next)) setCityState(next as CityId);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const setCity = (c: CityId | string) => {
    if (!isValidSlug(c)) return;
    setCityState(c as CityId);
    try { localStorage.setItem(KEY, c); } catch {}
  };

  return <Ctx.Provider value={{ city, setCity }}>{children}</Ctx.Provider>;
}

export function useCity() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useCity must be used inside CityProvider");
  return ctx;
}
